import { ResendCode } from "@faire/retailer-visitor-shared/components/AppSignIn/components/__internal__/ResendCode";
import { ContinueToNextViewButton } from "@faire/retailer-visitor-shared/components/AppSignIn/components/ContinueToNextViewButton";
import { convertErrorToLoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/convertErrorToLoginError";
import { logErrorSignIn } from "@faire/retailer-visitor-shared/components/AppSignIn/components/logErrorSignIn";
import { LoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/LoginError";
import { AllResult } from "@faire/retailer-visitor-shared/components/AppSignIn/components/SignInController";
import { LoginWrapper } from "@faire/retailer-visitor-shared/components/AppSignIn/components/styled";
import {
  isNumericOrBlank,
  isNumericOrBlankCode,
  VALID_CODE_LENGTH,
} from "@faire/retailer-visitor-shared/components/AppSignIn/components/VerificationInputUtils";
import { View } from "@faire/retailer-visitor-shared/components/AppSignIn/components/View";
import { useSignInEventTracker } from "@faire/retailer-visitor-shared/components/AppSignIn/useSignInEventTracker";
import { VerificationInput } from "@faire/retailer-visitor-shared/components/VerificationInput/VerificationInput";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Checkbox } from "@faire/web--source/slate/Checkbox";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import login from "@faire/web-api--source/endpoints/www/api/v2/users/login/post";
import { trackRetailerPortalLoginModalLoggedInWithSecondFactor } from "@faire/web-api--source/events/retailerPortal/actionCategoryOther/loginModalLoggedInWithSecondFactor";
import { trackRetailerPortalLoginModalLoginWithSecondFactor } from "@faire/web-api--source/events/retailerPortal/actionCategoryOther/loginModalLoginWithSecondFactor";
import { ILoginRequest } from "@faire/web-api--source/indigofair/data/ILoginRequest";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { MfaMethod } from "@faire/web-api--source/indigofair/data/MfaMethod";
import React, { FC, useState, useRef } from "react";
import styled from "styled-components";

interface IProps {
  email?: string;
  password?: string;
  userToken?: string;
  mfaTitle?: string;
  mfaDescription?: string;
  mfaMethod?: keyof typeof MfaMethod;
  onComplete: (result: AllResult) => void;
  onLogin: (user: IUser) => void;
}

export const EnterMFAView: FC<IProps> = ({
  email,
  password,
  userToken,
  mfaTitle,
  mfaDescription,
  mfaMethod,
  onComplete,
  onLogin,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { trackFieldValue } = useSignInEventTracker("ENTER_MFA");
  const [isLoadingAutoLogin, setIsLoadingAutoLogin] = useState(false);
  const [showResend, setShowResend] = useState(true);
  const code = useRef("");
  const [error, setError] = useState<LoginError>();
  const [passwordState, setPasswordState] = useState("");
  const [hasCodeInputError, setHasCodeInputError] = useState(false);
  const [verificationInputErrorMessage, setVerificationInputErrorMessage] =
    useState<string>();
  const [clearVerificationInput, setClearVerificationInput] = useState(false);
  const [suppressFutureMfa, setSuppressFutureMfa] = useState(true);

  const { isActive, ssoToken } = useSSOStore();

  const setCode = (codeValue: string) => {
    code.current = codeValue;
  };

  const handleShouldSuppressFutureMfaChange = () =>
    setSuppressFutureMfa(!suppressFutureMfa);

  const handleVerificationCodeInputChange = (
    codeValue: string,
    slotValue: string
  ) => {
    setCode(codeValue);
    setHasCodeInputError(false);
    setError(undefined);

    trackFieldValue("mfaCode", codeValue);

    if (!isNumericOrBlankCode(slotValue)) {
      setVerificationInputErrorMessage(
        strictLocalize({
          defaultMessage: "Enter numbers only.",
          description: {
            text: "Email or password that was entered is incorrect",
            img: "https://cdn.faire.com/fastly/52a1bf8c774d8b3fed237efa2a26b2870aeac3cf261e66a7694e6083f7a0398e.png",
          },
        })
      );
    } else if (isNumericOrBlankCode(code.current)) {
      setVerificationInputErrorMessage(undefined);
      if (isValidCode()) {
        onContinue();
      }
    }
  };

  const onResend = async () => {
    const mfa_method = mfaMethod ?? MfaMethod.EMAIL_ADDRESS; // Default resends to email address
    try {
      if (isActive) {
        await login(
          ILoginRequest.build({
            sso_token: ssoToken,
            resend: true,
            password: passwordState ? passwordState : password,
            enable_mfa_method_selection: true,
            mfa_method: mfa_method,
          })
        );
      } else {
        await login(
          ILoginRequest.build({
            email_address: email,
            user_token: !email ? userToken : undefined,
            resend: true,
            password: passwordState ? passwordState : password,
            enable_mfa_method_selection: true,
            mfa_method: mfa_method,
          })
        );
      }
      setShowResend(false);
    } catch (e) {
      logErrorSignIn(
        "ENTER_MFA",
        convertErrorToLoginError(e, strictLocalize).message
      );
      setError(convertErrorToLoginError(e, strictLocalize));
      setHasCodeInputError(false);
    }
  };

  const getUser = async () => {
    const request = isActive
      ? ILoginRequest.build({
          mfa_code: code.current,
          sso_token: ssoToken,
          password: passwordState ? passwordState : password,
          enable_mfa_method_selection: true,
          should_suppress_future_mfa: suppressFutureMfa,
        })
      : ILoginRequest.build({
          mfa_code: code.current,
          email_address: email,
          user_token: !email ? userToken : undefined,
          password: passwordState ? passwordState : password,
          enable_mfa_method_selection: true,
          should_suppress_future_mfa: suppressFutureMfa,
        });
    const { user } = await login(request);
    return user;
  };

  const onContinue = async () => {
    trackRetailerPortalLoginModalLoginWithSecondFactor();
    setError(undefined);
    setIsLoadingAutoLogin(true);
    try {
      const user = await getUser();
      if (!user) {
        setError(
          new LoginError(
            strictLocalize({
              defaultMessage: "Invalid credentials",
              description: {
                text: "Email or password that was entered is incorrect",
              },
            })
          )
        );
        return;
      }
      const nextView =
        user.missing_password &&
        // Skip create password if MFA and SSO are used
        !isActive
          ? View.CREATE_PASSWORD
          : View.LOADING;
      if (nextView === View.LOADING) {
        trackRetailerPortalLoginModalLoggedInWithSecondFactor();
        onLogin(user);
      }
      onComplete({ nextView, loggedInUser: user });
      setClearVerificationInput(false);
    } catch (e) {
      const error = convertErrorToLoginError(e, strictLocalize);
      logErrorSignIn("ENTER_MFA", error.message);
      setPasswordState("");
      setCode("");
      if (error.httpStatusCode === 400) {
        setError(
          new LoginError(
            strictLocalize({
              defaultMessage: "Invalid code",
              description: {
                text: "Error message shown to user if they enter the wrong code on MFA",
              },
            })
          )
        );
      }
      setHasCodeInputError(false);
      setIsLoadingAutoLogin(false);
      setClearVerificationInput(true);
    }
  };

  const isValidCode = (): boolean =>
    code.current.length === VALID_CODE_LENGTH &&
    !verificationInputErrorMessage &&
    !hasCodeInputError;

  return (
    <LoginWrapper>
      <Typography variant="displaySSerifRegular" align="left">
        {mfaTitle}
      </Typography>
      <Spacer height="1x" />
      <Typography align="left">{mfaDescription}</Typography>
      <Spacer height="3x" />
      <VerificationInput
        validateFn={isNumericOrBlank}
        onChange={handleVerificationCodeInputChange}
        errorMessage={verificationInputErrorMessage ?? error?.message}
        isValidInput={isValidCode()}
        errorAll={hasCodeInputError}
        value={code.current}
        ariaLabel={strictLocalize({
          defaultMessage: "Enter Verification Code",
          description: {
            text: "Verification input label to prompt user to enter the 6-digit code sent to their preferred MFA choice",
          },
        })}
        clearInput={clearVerificationInput}
      />
      <Spacer height="3x" />
      <ResendCode
        onResend={onResend}
        showResend={showResend}
        mfaMethod={mfaMethod}
      />
      <UserDeviceMfaSuppressionCheckbox
        checked={suppressFutureMfa}
        onChange={handleShouldSuppressFutureMfaChange}
        label={strictLocalize({
          defaultMessage: "Don't ask again for 30 days",
          description: {
            text: "Checkbox that asks if users want to disable future MFA verification attempts on the device they're using for 30 days.",
            img: "https://cdn.faire.com/fastly/62094b94211d2afe8c2fe8d6bb370a1043e1920b39aa4fd585c61b098abb937e.png",
          },
        })}
      />
      <Spacer height="4x" />
      <ContinueToNextViewButton
        onClick={onContinue}
        isLoading={isLoadingAutoLogin}
        disabled={!isValidCode()}
      />
    </LoginWrapper>
  );
};

const UserDeviceMfaSuppressionCheckbox = styled(Checkbox)`
  margin-top: ${getSpacing("2x")};
`;
